import React, { useEffect, useState, useRef } from 'react';
import { 
  MessageSquare, 
  Receipt, 
  Send, 
  CheckCircle2, 
  AlertCircle, 
  Upload, 
  FileText, 
  X, 
  Loader2, 
  Info,
  Calendar,
  Building,
  Mail,
} from 'lucide-react';
import { contentEs } from '../data/contentEs.js';
import { contentEn } from '../data/contentEn.js';
import { publicLocations } from '../data/locations.js';
import { 
  submitContactForm, 
  submitBillingForm,
  isValidMexicanPhone,
  ContactFormData,
  BillingFormData 
} from '../services/websiteFormsService.js';

interface InvoicePageProps {
  isEn?: boolean;
}

export const InvoicePage: React.FC<InvoicePageProps> = ({ isEn = false }) => {
  const c = isEn ? contentEn : contentEs;
  const inv = c.invoice;

  useEffect(() => {
    document.title = c.meta.invoice.title;
    window.scrollTo(0, 0);
  }, [c.meta.invoice.title]);

  // ==========================================
  // TARJETA A: FORMULARIO DE CONTACTO
  // ==========================================
  const [contactData, setContactData] = useState<ContactFormData>({
    nombre: '',
    empresa: '',
    correo: '',
    telefono: '',
    motivo: inv.contact.reasons[0],
    mensaje: '',
    _hp: '',
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError(null);

    if (!contactData.nombre.trim() || !contactData.correo.trim() || !contactData.mensaje.trim()) {
      setContactError(isEn ? 'Please fill in all required fields.' : 'Por favor completa todos los campos obligatorios.');
      return;
    }

    if (!contactData.correo.includes('@') || !contactData.correo.includes('.')) {
      setContactError(isEn ? 'Please provide a valid email.' : 'Por favor proporciona un correo electrónico válido.');
      return;
    }

    setContactLoading(true);
    const res = await submitContactForm(contactData);
    setContactLoading(false);

    if (res.success) {
      setContactSuccess(true);
      setContactData({
        nombre: '',
        empresa: '',
        correo: '',
        telefono: '',
        motivo: inv.contact.reasons[0],
        mensaje: '',
        _hp: '',
      });
    } else {
      setContactError(res.error || (isEn ? 'Could not submit message. Please try again.' : 'No se pudo enviar el mensaje. Por favor intenta de nuevo.'));
    }
  };

  // ==========================================
  // TARJETA B: FORMULARIO DE FACTURACIÓN
  // ==========================================
  const [billingData, setBillingData] = useState<Omit<BillingFormData, 'ticketFile'>>({
    razonSocial: '',
    rfc: '',
    regimenFiscal: inv.billing.regimes[0],
    codigoPostal: '',
    usoCfdi: inv.billing.cfdiUses[0],
    correo: '',
    telefono: '',
    sucursal: publicLocations[0]?.name || 'Azueta',
    fechaConsumo: '',
    folioTicket: '',
    montoTotal: '',
    formaPago: '',
    notas: '',
    _hp: '',
  });
  const [ticketFile, setTicketFile] = useState<File | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSuccess, setBillingSuccess] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setBillingError(isEn ? 'File size exceeds 5 MB limit.' : 'El archivo excede el límite de 5 MB.');
        return;
      }
      setBillingError(null);
      setTicketFile(file);
    }
  };

  const removeTicketFile = () => {
    setTicketFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBillingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (billingLoading) return;
    setBillingError(null);

    // Validaciones
    if (
      !billingData.razonSocial.trim() ||
      !billingData.rfc.trim() ||
      !billingData.codigoPostal.trim() ||
      !billingData.correo.trim() ||
      !billingData.sucursal.trim() ||
      !billingData.fechaConsumo.trim() ||
      !billingData.folioTicket.trim() ||
      !billingData.montoTotal.trim()
    ) {
      setBillingError(isEn ? 'Please fill in all required fields.' : 'Por favor completa todos los campos obligatorios.');
      return;
    }

    if (!billingData.formaPago.trim()) {
      setBillingError(isEn ? 'Please select a payment method.' : 'Por favor selecciona la forma de pago.');
      return;
    }

    if (billingData.telefono && !isValidMexicanPhone(billingData.telefono)) {
      setBillingError(
        isEn
          ? 'Please enter a valid 10-digit Mexican phone number (optional +52 prefix).'
          : 'Por favor ingresa un número de teléfono válido de 10 dígitos (prefijo +52 opcional).'
      );
      return;
    }

    if (billingData.notas && billingData.notas.length > 1000) {
      setBillingError(
        isEn
          ? 'Additional notes cannot exceed 1,000 characters.'
          : 'Las notas adicionales no pueden exceder 1,000 caracteres.'
      );
      return;
    }

    if (!ticketFile) {
      setBillingError(isEn ? 'Please attach your consumption ticket (image or PDF).' : 'Por favor adjunta tu ticket de consumo (imagen o PDF).');
      return;
    }

    const cleanRfc = billingData.rfc.trim().toUpperCase();
    if (cleanRfc.length < 12 || cleanRfc.length > 13) {
      setBillingError(isEn ? 'RFC must be 12 (legal entity) or 13 (individual) characters.' : 'El RFC debe contener 12 (persona moral) o 13 (persona física) caracteres.');
      return;
    }

    if (billingData.codigoPostal.trim().length !== 5) {
      setBillingError(isEn ? 'Fiscal postal code must be exactly 5 digits.' : 'El código postal fiscal debe contener 5 dígitos.');
      return;
    }

    setBillingLoading(true);
    const res = await submitBillingForm({
      ...billingData,
      rfc: cleanRfc,
      ticketFile,
    });
    setBillingLoading(false);

    if (res.success) {
      setBillingSuccess(true);
      setBillingData({
        razonSocial: '',
        rfc: '',
        regimenFiscal: inv.billing.regimes[0],
        codigoPostal: '',
        usoCfdi: inv.billing.cfdiUses[0],
        correo: '',
        telefono: '',
        sucursal: publicLocations[0]?.name || 'Azueta',
        fechaConsumo: '',
        folioTicket: '',
        montoTotal: '',
        formaPago: '',
        notas: '',
        _hp: '',
      });
      removeTicketFile();
    } else {
      setBillingError(res.error || (isEn ? 'Could not submit invoice request. Please try again.' : 'No se pudo enviar la solicitud de factura. Por favor intenta de nuevo.'));
    }
  };

  return (
    <div className="bg-[#FAF8F5] min-h-screen py-14 sm:py-20 lg:py-24">
      {/* ========================================================
          ENCABEZADO
      ======================================================== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="text-xs uppercase font-extrabold tracking-widest text-[#97472E] bg-[#F1ECE1] px-3.5 py-1.5 rounded-full inline-block">
            {inv.eyebrow}
          </span>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-[#001557] tracking-tight">
            {inv.title}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            {inv.subtitle}
          </p>
        </div>

        {/* ========================================================
            CONTENEDOR DE DOS TARJETAS (2 COLUMNAS DESKTOP / 1 MOBILE)
        ======================================================== */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          
          {/* ======================================================
              TARJETA A: CONTACTO ("HABLEMOS")
          ====================================================== */}
          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm hover:shadow-md transition-shadow p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3.5 pb-5 border-b border-[#F0ECE4]">
              <div className="w-11 h-11 rounded-xl bg-[#001557]/5 text-[#001557] flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-[#001557]" />
              </div>
              <div>
                <h2 className="font-heading font-black text-2xl text-[#001557]">
                  {inv.contact.title}
                </h2>
                <span className="text-xs font-semibold text-gray-500">
                  {isEn ? 'General inquiries & customer support' : 'Preguntas generales y atención a clientes'}
                </span>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-600 leading-relaxed">
              {inv.contact.text}
            </p>

            {contactSuccess ? (
              <div className="mt-8 p-6 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  <h4 className="font-heading font-bold text-base text-emerald-900">
                    {isEn ? 'Message Sent' : 'Mensaje Enviado'}
                  </h4>
                </div>
                <p className="text-sm text-emerald-800 leading-relaxed">
                  {inv.contact.successMsg}
                </p>
                <button
                  type="button"
                  onClick={() => setContactSuccess(false)}
                  className="mt-2 text-xs font-bold text-emerald-700 underline hover:text-emerald-900"
                >
                  {isEn ? 'Send another message' : 'Enviar otro mensaje'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="mt-6 space-y-4">
                {/* Honeypot */}
                <input
                  type="text"
                  name="_hp"
                  value={contactData._hp}
                  onChange={(e) => setContactData({ ...contactData, _hp: e.target.value })}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Full Name' : 'Nombre completo'} <span className="text-[#97472E]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={contactData.nombre}
                      onChange={(e) => setContactData({ ...contactData, nombre: e.target.value })}
                      placeholder={isEn ? 'Your name' : 'Ej. María González'}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Company' : 'Empresa'} <span className="text-gray-400 font-normal">({isEn ? 'Optional' : 'Opcional'})</span>
                    </label>
                    <input
                      type="text"
                      value={contactData.empresa}
                      onChange={(e) => setContactData({ ...contactData, empresa: e.target.value })}
                      placeholder={isEn ? 'Company name' : 'Nombre de tu empresa'}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Email' : 'Correo electrónico'} <span className="text-[#97472E]">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={contactData.correo}
                      onChange={(e) => setContactData({ ...contactData, correo: e.target.value })}
                      placeholder={isEn ? 'name@example.com' : 'maria@ejemplo.com'}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Phone' : 'Teléfono'} <span className="text-gray-400 font-normal">({isEn ? 'Optional' : 'Opcional'})</span>
                    </label>
                    <input
                      type="tel"
                      value={contactData.telefono}
                      onChange={(e) => setContactData({ ...contactData, telefono: e.target.value })}
                      placeholder={isEn ? '55 1234 5678' : '55 1234 5678'}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    {isEn ? 'Reason for contact' : 'Motivo de contacto'} <span className="text-[#97472E]">*</span>
                  </label>
                  <select
                    value={contactData.motivo}
                    onChange={(e) => setContactData({ ...contactData, motivo: e.target.value })}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                  >
                    {inv.contact.reasons.map((r: string) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                    {isEn ? 'Message' : 'Mensaje'} <span className="text-[#97472E]">*</span>
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={contactData.mensaje}
                    onChange={(e) => setContactData({ ...contactData, mensaje: e.target.value })}
                    placeholder={isEn ? 'How can we help you?' : 'Escribe aquí tu duda, comentario o solicitud...'}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557] resize-none"
                  />
                </div>

                {contactError && (
                  <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <span>{contactError}</span>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#001557] hover:bg-[#11255E] text-white font-bold py-3.5 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {contactLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{inv.contact.sendingBtn}</span>
                      </>
                    ) : (
                      <>
                        <span>{inv.contact.btn}</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* ======================================================
              TARJETA B: FACTURACIÓN ("SOLICITA TU FACTURA")
          ====================================================== */}
          <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm hover:shadow-md transition-shadow p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3.5 pb-5 border-b border-[#F0ECE4]">
              <div className="w-11 h-11 rounded-xl bg-[#001557]/5 text-[#001557] flex items-center justify-center shrink-0">
                <Receipt className="w-5 h-5 text-[#001557]" />
              </div>
              <div>
                <h2 className="font-heading font-black text-2xl text-[#001557]">
                  {inv.billing.title}
                </h2>
                <span className="text-xs font-semibold text-gray-500">
                  {isEn ? 'Tax invoice request (CFDI)' : 'Solicitud de comprobante fiscal digital'}
                </span>
              </div>
            </div>

            <p className="mt-4 text-sm text-gray-600 leading-relaxed">
              {inv.billing.text}
            </p>

            {/* NOTA INFORMATIVA OBLIGATORIA */}
            <div className="mt-4 p-4 rounded-xl bg-[#FAF8F5] border border-[#E8E4DC] flex items-start gap-3">
              <Info className="w-4 h-4 text-[#001557] shrink-0 mt-0.5" />
              <p className="text-xs text-gray-600 leading-relaxed">
                {inv.billing.notice}
              </p>
            </div>

            {billingSuccess ? (
              <div className="mt-8 p-6 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                  <h4 className="font-heading font-bold text-base text-emerald-900">
                    {isEn ? 'Request Received' : 'Solicitud Recibida'}
                  </h4>
                </div>
                <p className="text-sm text-emerald-800 leading-relaxed">
                  {inv.billing.successMsg}
                </p>
                <button
                  type="button"
                  onClick={() => setBillingSuccess(false)}
                  className="mt-2 text-xs font-bold text-emerald-700 underline hover:text-emerald-900"
                >
                  {isEn ? 'Submit another invoice request' : 'Enviar otra solicitud de factura'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleBillingSubmit} className="mt-6 space-y-6">
                {/* Honeypot */}
                <input
                  type="text"
                  name="_hp"
                  value={billingData._hp}
                  onChange={(e) => setBillingData({ ...billingData, _hp: e.target.value })}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />

                {/* --- SECCIÓN 1: DATOS FISCALES --- */}
                <div className="space-y-4 pt-1">
                  <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-[#001557]">
                    <Building className="w-3.5 h-3.5" />
                    <span>{isEn ? '1. Fiscal Information' : '1. Información fiscal'}</span>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Legal Name / Full Name' : 'Nombre o razón social'} <span className="text-[#97472E]">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={billingData.razonSocial}
                      onChange={(e) => setBillingData({ ...billingData, razonSocial: e.target.value })}
                      placeholder={isEn ? 'Exact legal company name' : 'Ej. Alimentos del Centro S.A. de C.V.'}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        RFC <span className="text-[#97472E]">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={13}
                        value={billingData.rfc}
                        onChange={(e) => setBillingData({ ...billingData, rfc: e.target.value.toUpperCase() })}
                        placeholder="XAXX010101000"
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Fiscal Zip Code' : 'Código postal fiscal'} <span className="text-[#97472E]">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        maxLength={5}
                        pattern="[0-9]{5}"
                        value={billingData.codigoPostal}
                        onChange={(e) => setBillingData({ ...billingData, codigoPostal: e.target.value.replace(/\D/g, '') })}
                        placeholder="06000"
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Tax Regime' : 'Régimen fiscal'} <span className="text-[#97472E]">*</span>
                      </label>
                      <select
                        value={billingData.regimenFiscal}
                        onChange={(e) => setBillingData({ ...billingData, regimenFiscal: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      >
                        {inv.billing.regimes.map((reg: string) => (
                          <option key={reg} value={reg}>
                            {reg}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'CFDI Purpose' : 'Uso de CFDI'} <span className="text-[#97472E]">*</span>
                      </label>
                      <select
                        value={billingData.usoCfdi}
                        onChange={(e) => setBillingData({ ...billingData, usoCfdi: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      >
                        {inv.billing.cfdiUses.map((u: string) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* --- SECCIÓN 2: CONTACTO --- */}
                <div className="space-y-4 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-[#001557]">
                    <Mail className="w-3.5 h-3.5" />
                    <span>{isEn ? '2. Invoicing Contact' : '2. Contacto para recibir factura'}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Email to receive invoice' : 'Correo electrónico para recibir la factura'} <span className="text-[#97472E]">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={billingData.correo}
                        onChange={(e) => setBillingData({ ...billingData, correo: e.target.value })}
                        placeholder={isEn ? 'invoices@company.com' : 'facturacion@tuempresa.com'}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>

                    <div>
                      <label htmlFor="billing-phone" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Contact phone (optional)' : 'Teléfono de contacto (opcional)'}
                      </label>
                      <input
                        id="billing-phone"
                        type="tel"
                        value={billingData.telefono}
                        onChange={(e) => setBillingData({ ...billingData, telefono: e.target.value })}
                        placeholder={isEn ? '+52 55 1234 5678' : '+52 55 1234 5678'}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>
                  </div>
                </div>

                {/* --- SECCIÓN 3: INFORMACIÓN DEL CONSUMO --- */}
                <div className="space-y-4 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-[#001557]">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{isEn ? '3. Receipt Information' : '3. Información del consumo'}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Branch location' : 'Sucursal del consumo'} <span className="text-[#97472E]">*</span>
                      </label>
                      <select
                        value={billingData.sucursal}
                        onChange={(e) => setBillingData({ ...billingData, sucursal: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      >
                        {publicLocations.map((loc) => (
                          <option key={loc.id} value={loc.name}>
                            {loc.name} ({loc.neighborhood})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Date of consumption' : 'Fecha del consumo'} <span className="text-[#97472E]">*</span>
                      </label>
                      <input
                        type="date"
                        required
                        max={new Date().toISOString().split('T')[0]}
                        value={billingData.fechaConsumo}
                        onChange={(e) => setBillingData({ ...billingData, fechaConsumo: e.target.value })}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Ticket Folio / Number' : 'Folio del ticket'} <span className="text-[#97472E]">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={billingData.folioTicket}
                        onChange={(e) => setBillingData({ ...billingData, folioTicket: e.target.value })}
                        placeholder={isEn ? 'e.g. T-12495' : 'Ej. T-12495'}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                        {isEn ? 'Total Amount (MXN)' : 'Monto total (MXN)'} <span className="text-[#97472E]">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 font-bold text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          required
                          step="0.01"
                          min="0"
                          value={billingData.montoTotal}
                          onChange={(e) => setBillingData({ ...billingData, montoTotal: e.target.value })}
                          placeholder="0.00"
                          className="w-full pl-8 pr-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="billing-payment-method" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Payment method' : 'Forma de pago'} <span className="text-[#97472E]">*</span>
                    </label>
                    <select
                      id="billing-payment-method"
                      required
                      value={billingData.formaPago}
                      onChange={(e) => setBillingData({ ...billingData, formaPago: e.target.value })}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557]"
                    >
                      <option value="" disabled>
                        {isEn ? 'Select a payment method' : 'Selecciona una forma de pago'}
                      </option>
                      <option value="Transferencia">Transferencia</option>
                      <option value="Efectivo">Efectivo</option>
                      <option value="Tarjeta de crédito">Tarjeta de crédito</option>
                      <option value="Tarjeta de débito">Tarjeta de débito</option>
                    </select>
                  </div>

                  {/* ADJUNTAR TICKET */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Attach receipt ticket (Image or PDF)' : 'Adjuntar ticket de consumo (Imagen o PDF)'} <span className="text-[#97472E]">*</span>
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      required={!ticketFile}
                      accept="image/png,image/jpeg,image/webp,image/jpg,application/pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="ticket-upload"
                    />

                    {ticketFile ? (
                      <div className="flex items-center justify-between p-3.5 rounded-xl bg-blue-50/70 border border-blue-200">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <FileText className="w-5 h-5 text-[#001557] shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-[#001557] truncate">
                              {ticketFile.name}
                            </p>
                            <span className="text-[11px] text-gray-500">
                              {(ticketFile.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeTicketFile}
                          className="p-1 rounded-lg hover:bg-blue-100 text-gray-500 hover:text-red-600 transition-colors"
                          title={isEn ? 'Remove file' : 'Eliminar archivo'}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="ticket-upload"
                        className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 hover:border-[#001557] rounded-xl cursor-pointer bg-gray-50/50 hover:bg-gray-50 transition-colors"
                      >
                        <Upload className="w-6 h-6 text-gray-400 mb-2" />
                        <span className="text-xs font-bold text-[#001557]">
                          {isEn ? 'Click to select ticket' : 'Selecciona o arrastra tu ticket'}
                        </span>
                        <span className="text-[11px] text-gray-400 mt-1">
                          {isEn ? 'JPG, PNG or PDF (Max 5 MB)' : 'Formatos: JPG, PNG o PDF (Máximo 5 MB)'}
                        </span>
                      </label>
                    )}
                  </div>

                  {/* NOTAS ADICIONALES */}
                  <div>
                    <label htmlFor="billing-notes" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                      {isEn ? 'Additional notes (optional)' : 'Notas adicionales (opcional)'}
                    </label>
                    <textarea
                      id="billing-notes"
                      rows={3}
                      maxLength={1000}
                      value={billingData.notas}
                      onChange={(e) => setBillingData({ ...billingData, notas: e.target.value })}
                      placeholder={isEn ? 'Any special instructions for your invoice? Write them here.' : '¿Hay alguna indicación especial para tu factura? Escríbela aquí.'}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#001557] focus:border-[#001557] resize-none"
                    />
                    <div className="flex justify-end mt-1">
                      <span className="text-[11px] text-gray-400">
                        {billingData.notas?.length || 0}/1000
                      </span>
                    </div>
                  </div>
                </div>

                {billingError && (
                  <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <span>{billingError}</span>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={billingLoading}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#001557] hover:bg-[#11255E] text-white font-bold py-3.5 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {billingLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{inv.billing.sendingBtn}</span>
                      </>
                    ) : (
                      <>
                        <span>{inv.billing.btn}</span>
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
